import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ArrowRightLeft, Package, Search, Loader2, Check, X, AlertTriangle, Scan } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';

import { useStore } from '../context/StoreContext';
const StockTransfer = () => {
  const { storeName } = useStore();
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Transfer direction (locked for the whole draft session)
  const [fromStore, setFromStore] = useState('orewa');
  const [toStore, setToStore] = useState('eastridge');

  // Draft list of items
  const [draftItems, setDraftItems] = useState([]); // { product_id, name, barcode, qty }
  const [notes, setNotes] = useState('');

  // Scan/search
  const [scanInput, setScanInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const scanRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, transfersRes] = await Promise.all([
        axios.get(`${API_URL}/products?limit=15000`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/stock-transfers`, { headers: getAuthHeader() })
      ]);
      setProducts(productsRes.data);
      setTransfers(transfersRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    // Keep focus on scan input as the staff's main interaction point
    if (!loading) scanRef.current?.focus();
  }, [loading]);

  const addProductToDraft = useCallback((product) => {
    setDraftItems(prev => {
      const idx = prev.findIndex(it => it.product_id === product.id);
      if (idx >= 0) {
        return prev.map((it, i) => i === idx ? { ...it, qty: it.qty + 1 } : it);
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          barcode: product.barcode || '',
          qty: 1,
        }
      ];
    });
  }, []);

  // Scan handler: looks up by exact barcode first, otherwise opens search results
  const handleScanEnter = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const v = scanInput.trim();
    if (!v) return;
    const exact = products.find(p => p.barcode && p.barcode === v);
    if (exact) {
      addProductToDraft(exact);
      setScanInput('');
      setSearchResults([]);
      toast.success(`Added ${exact.name}`, { duration: 1500 });
      return;
    }
    const ql = v.toLowerCase();
    const matches = products.filter(p =>
      (p.name || '').toLowerCase().includes(ql) ||
      (p.barcode || '').includes(v) ||
      (p.sku || '').toLowerCase().includes(ql)
    ).slice(0, 15);
    if (matches.length === 0) {
      toast.error(`No product found for "${v}"`);
    } else if (matches.length === 1) {
      addProductToDraft(matches[0]);
      setScanInput('');
      setSearchResults([]);
      toast.success(`Added ${matches[0].name}`, { duration: 1500 });
    } else {
      setSearchResults(matches);
    }
  };

  const updateDraftQty = (idx, qty) => {
    setDraftItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: Math.max(0, parseInt(qty) || 0) } : it));
  };

  const removeDraftItem = (idx) => {
    setDraftItems(prev => prev.filter((_, i) => i !== idx));
  };

  const swapStores = () => {
    setFromStore(toStore);
    setToStore(fromStore);
  };

  const productById = (id) => products.find(p => p.id === id);
  const fromStock = (id) => (productById(id) || {})[`stock_${fromStore}`] || 0;
  const toStock = (id) => (productById(id) || {})[`stock_${toStore}`] || 0;

  const draftErrors = draftItems
    .map((it, idx) => {
      if (!it.qty || it.qty <= 0) return { idx, msg: 'Enter qty > 0' };
      if (it.qty > fromStock(it.product_id)) return { idx, msg: `Only ${fromStock(it.product_id)} at ${fromStore}` };
      return null;
    })
    .filter(Boolean);

  const totalItems = draftItems.length;
  const totalUnits = draftItems.reduce((s, it) => s + (it.qty || 0), 0);

  const handleConfirm = async () => {
    if (draftItems.length === 0) {
      toast.error('Scan some products first');
      return;
    }
    if (fromStore === toStore) {
      toast.error('From and To must be different stores');
      return;
    }
    if (draftErrors.length > 0) {
      toast.error('Fix the highlighted rows before confirming');
      return;
    }
    setSubmitting(true);
    let okCount = 0;
    const failures = [];
    for (const it of draftItems) {
      try {
        await axios.post(`${API_URL}/stock-transfer`, {
          product_id: it.product_id,
          from_store: fromStore,
          to_store: toStore,
          quantity: it.qty,
          notes: notes || ''
        }, { headers: getAuthHeader() });
        okCount++;
      } catch (e) {
        failures.push({ name: it.name, error: e.response?.data?.detail || 'Failed' });
      }
    }
    setSubmitting(false);
    if (okCount > 0) {
      toast.success(`Transferred ${okCount} line${okCount === 1 ? '' : 's'} to ${storeName(toStore)}`);
    }
    if (failures.length > 0) {
      toast.error(`${failures.length} failed: ${failures.slice(0, 3).map(f => f.name).join(', ')}`);
    }
    setDraftItems([]);
    setNotes('');
    setScanInput('');
    setSearchResults([]);
    await fetchData();
    scanRef.current?.focus();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const storeLabel = (s) => storeName(s);

  return (
    <div className="space-y-6" data-testid="stock-transfer-page">
      <div>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">Stock Transfer</h1>
        <p className="text-slate-500 mt-1">Scan to build a transfer list, then confirm once</p>
      </div>

      {/* Direction + scan */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ArrowRightLeft className="w-5 h-5 text-blue-500" />
            Transfer Direction
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">From</Label>
              <Select value={fromStore} onValueChange={setFromStore}>
                <SelectTrigger className="w-40" data-testid="transfer-from-store">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="orewa">{storeName('orewa')}</SelectItem>
                  <SelectItem value="eastridge">{storeName('eastridge')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="icon" onClick={swapStores} className="mb-0.5" data-testid="transfer-swap-stores">
              <ArrowRightLeft className="w-4 h-4" />
            </Button>
            <div>
              <Label className="text-xs">To</Label>
              <Select value={toStore} onValueChange={setToStore}>
                <SelectTrigger className="w-40" data-testid="transfer-to-store">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="orewa">{storeName('orewa')}</SelectItem>
                  <SelectItem value="eastridge">{storeName('eastridge')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs">Scan or search product</Label>
              <div className="relative">
                <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  ref={scanRef}
                  placeholder="Scan barcode or type name…"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={handleScanEnter}
                  className="pl-10 h-10 text-base"
                  data-testid="transfer-scan-input"
                  autoFocus
                />
              </div>
              {searchResults.length > 0 && (
                <div className="border rounded-md mt-1 max-h-56 overflow-y-auto bg-white shadow-md" data-testid="transfer-search-results">
                  {searchResults.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { addProductToDraft(p); setScanInput(''); setSearchResults([]); scanRef.current?.focus(); }}
                      className="w-full p-2.5 hover:bg-blue-50 flex items-center justify-between text-left border-b last:border-b-0"
                      data-testid={`transfer-search-result-${p.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{p.name}</p>
                        <p className="text-xs text-slate-400">{p.barcode || 'no barcode'}</p>
                      </div>
                      <div className="text-right text-xs shrink-0 ml-2">
                        <p className="text-blue-600">O: {p.stock_orewa || 0}</p>
                        <p className="text-emerald-600">E: {p.stock_eastridge || 0}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Draft list */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="w-5 h-5 text-slate-500" />
            Pending Transfer · {storeLabel(fromStore)} → {storeLabel(toStore)}
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-500" data-testid="transfer-totals">
              <span className="font-semibold text-slate-900">{totalItems}</span> products · <span className="font-semibold text-slate-900">{totalUnits}</span> units
            </div>
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              onClick={handleConfirm}
              disabled={submitting || draftItems.length === 0 || draftErrors.length > 0}
              data-testid="transfer-accept-top"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Accepting…</> : <><Check className="w-4 h-4 mr-1" /> Accept Transfer</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {draftItems.length === 0 ? (
            <div className="text-center py-10 text-slate-400" data-testid="transfer-empty-state">
              <Scan className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Scan a barcode or search to add products to this transfer</p>
              <p className="text-xs mt-1">Once you add items, click <span className="font-semibold text-emerald-700">Accept Transfer</span> at the top right to confirm</p>
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Product</TableHead>
                    <TableHead className="text-xs text-center w-28">{storeLabel(fromStore)} stock</TableHead>
                    <TableHead className="text-xs text-center w-28">{storeLabel(toStore)} stock</TableHead>
                    <TableHead className="text-xs text-center w-28">Transfer qty</TableHead>
                    <TableHead className="text-xs w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draftItems.map((it, idx) => {
                    const err = draftErrors.find(e => e.idx === idx);
                    const oStock = fromStock(it.product_id);
                    const dStock = toStock(it.product_id);
                    return (
                      <TableRow key={it.product_id} className={err ? 'bg-red-50' : ''} data-testid={`transfer-draft-row-${idx}`}>
                        <TableCell>
                          <p className="font-medium text-sm">{it.name}</p>
                          <p className="text-xs text-slate-400">{it.barcode || '—'}</p>
                          {err && (
                            <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> {err.msg}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={oStock < it.qty ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700'}>
                            {oStock}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{dStock}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="1"
                            max={oStock}
                            value={it.qty}
                            onChange={(e) => updateDraftQty(idx, e.target.value)}
                            className={`w-20 mx-auto text-center ${err ? 'border-red-400' : ''}`}
                            data-testid={`transfer-draft-qty-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50" onClick={() => removeDraftItem(idx)} data-testid={`transfer-draft-remove-${idx}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Notes + bottom action row (also shown for muscle memory) */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs">Notes (applied to every line)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Eastridge low on stock"
                data-testid="transfer-notes"
                disabled={draftItems.length === 0}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setDraftItems([]); setNotes(''); scanRef.current?.focus(); }}
                disabled={submitting || draftItems.length === 0}
                data-testid="transfer-clear"
              >
                Clear list
              </Button>
              <Button
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm min-w-[200px]"
                onClick={handleConfirm}
                disabled={submitting || draftItems.length === 0 || draftErrors.length > 0}
                data-testid="transfer-accept"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Accepting…</> : <><Check className="w-4 h-4 mr-1" /> Accept Transfer ({totalUnits} units)</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="w-5 h-5 text-slate-500" />
            Recent Transfers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transfers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ArrowRightLeft className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No transfers yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">{formatDate(t.created_at)}</TableCell>
                    <TableCell className="font-medium">{t.product_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">{storeName(t.from_store)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{storeName(t.to_store)}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-bold">{t.quantity}</TableCell>
                    <TableCell className="text-sm text-slate-500">{t.notes || '-'}</TableCell>
                    <TableCell className="text-sm">{t.created_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StockTransfer;
